import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import { remoteLog } from "~/common/logging";
import { Button } from "~/components/ui/button";
import { CenteredDialog } from "~/components/ui/centered-dialog";
import { LoadingButton } from "~/components/ui/loading-button";
import { API_MANUALS_GET_STATIC } from "~/config";
import { useLoadingDelay } from "~/hooks/use-loading-delay";

import { ToastCloseIntent, ToastTypeTag, useCloseToastMutation } from "./toasts-api";

const PRICE_LIST_UPDATE_JUNE_2025_MANUAL_ID = 49;

export function PriceListUpdateDialog({ isOpen: isOpenProp }: { isOpen: boolean }) {
  const [isOpen, setIsOpen] = useState(true);
  const [closeToast, closeToastResult] = useCloseToastMutation();
  const displayLoading = useLoadingDelay(closeToastResult.isLoading, { delay: 200 });

  const isCloseLoading =
    displayLoading && closeToastResult.originalArgs?.intent == ToastCloseIntent.CLOSE;

  async function handleClick(intent: ToastCloseIntent) {
    if (closeToastResult.isLoading) {
      return;
    }
    try {
      await closeToast({ tag: ToastTypeTag.PRICE_LIST_UPDATE_JUNE_2025, intent }).unwrap();
    } catch (err) {
      remoteLog(err, "price_list_update_june_2025_dialog");
    }
  }

  function handleAction() {
    void handleClick(ToastCloseIntent.ACTION);
    setIsOpen(false);
  }

  function handleClose() {
    void handleClick(ToastCloseIntent.CLOSE);
    setIsOpen(false);
  }

  return (
    <CenteredDialog
      isOpen={isOpen && isOpenProp}
      onOpenChange={setIsOpen}
      closeProps={{ onClick: handleClose }}
      className="tw-space-y-4"
    >
      <div>Уважаемые партнёры!</div>

      <div>
        С радостью сообщаем, что в скором времени для вас будут доступны обновленные курсы 3D Smile
        – теперь они стали еще удобнее и эффективнее!
      </div>

      <div className="tw-font-bold">Что изменилось:</div>

      <ul className="tw-space-y-2.5 tw-pl-3">
        <li>
          Индивидуальный подход – новое наполнение курсов 3D Smile лучше учитывает потребности
          каждого врача и пациента.
        </li>

        <li>
          Повышена клиническая эффективность – обновленные протоколы для ещё более впечатляющих
          результатов.
        </li>

        <li>
          Оптимизированные сроки – теперь вы сможете достигать целей быстрее и с максимальной
          выгодой для клиники и врача.
        </li>
      </ul>

      <div>
        Мы учли все ваши пожелания, внедрили значительные улучшения, поэтому произошла
        незначительная корректировка стоимости. Ознакомиться с новым наполнением курсов и
        стоимостью, которые вступят в силу с 01 июля 2025 года, вы можете в Личном кабинете, перейдя
        по{" "}
        <a
          href={API_MANUALS_GET_STATIC("document", PRICE_LIST_UPDATE_JUNE_2025_MANUAL_ID)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleAction}
        >
          ссылке
        </a>
        .
      </div>

      <div>Благодарим за ваше доверие и совместную работу!</div>

      <div>С уважением, 3D Smile</div>

      <div className="tw-flex tw-gap-2">
        <Button
          type="submit"
          variant="primary"
          size="1.5xl"
          rounded
          outline={false}
          className="tw-min-w-[130px] max-[600px]:tw-w-full"
          onClick={handleAction}
          asChild
        >
          <a
            href={API_MANUALS_GET_STATIC("document", PRICE_LIST_UPDATE_JUNE_2025_MANUAL_ID)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id="learn_more" />
          </a>
        </Button>

        <LoadingButton
          type="button"
          variant="secondary"
          size="1.5xl"
          rounded
          outline={false}
          className="tw-min-w-[130px] max-[600px]:tw-w-full"
          isLoading={isCloseLoading}
          onClick={handleClose}
        >
          <FormattedMessage id="alert.close" />
        </LoadingButton>
      </div>
    </CenteredDialog>
  );
}
